import * as THREE from 'three'
import { isObject, readValidatedJSON, type Validator } from './safeStorage'

export interface CameraState {
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  zoom: number
}

export interface SelectedPoint {
  id: string
  position: { x: number; y: number; z: number }
  timestamp: number
}

export interface ObjectTransform {
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number; z: number }
}

export interface SceneObjectState {
  id: string
  visible: boolean
  transform?: ObjectTransform
}

export interface GirasolesViewerState {
  camera: CameraState
  selectedPoints: SelectedPoint[]
  sceneObjects: SceneObjectState[]
  selectedTool: string | null
  timestamp: number
  version: string
}

const STORAGE_KEY = 'girasoles_viewer_state'
const STATE_VERSION = '1.0.0'

const isVec3 = (v: unknown): v is { x: number; y: number; z: number } =>
  isObject(v) && typeof v.x === 'number' && typeof v.y === 'number' && typeof v.z === 'number'

const isCameraState: Validator<CameraState> = ((v: unknown): v is CameraState =>
  isObject(v) && isVec3(v.position) && isVec3(v.rotation) && typeof v.zoom === 'number') as Validator<CameraState>

const isSelectedPoint: Validator<SelectedPoint> = ((v: unknown): v is SelectedPoint =>
  isObject(v) && typeof v.id === 'string' && isVec3(v.position) && typeof v.timestamp === 'number') as Validator<SelectedPoint>

const isSceneObjectState: Validator<SceneObjectState> = ((v: unknown): v is SceneObjectState =>
  isObject(v) && typeof v.id === 'string' && typeof v.visible === 'boolean') as Validator<SceneObjectState>

const isGirasolesViewerState: Validator<GirasolesViewerState> = ((v: unknown): v is GirasolesViewerState =>
  isObject(v) &&
  isCameraState(v.camera) &&
  Array.isArray(v.selectedPoints) && v.selectedPoints.every(isSelectedPoint) &&
  Array.isArray(v.sceneObjects) && v.sceneObjects.every(isSceneObjectState) &&
  (v.selectedTool === null || typeof v.selectedTool === 'string') &&
  typeof v.timestamp === 'number' &&
  typeof v.version === 'string') as Validator<GirasolesViewerState>

/**
 * Save the current viewer state to localStorage
 */
export const saveViewerState = (
  camera: THREE.PerspectiveCamera,
  selectedPoints: SelectedPoint[],
  sceneObjects: any[],
  selectedTool: string | null
): void => {
  try {
    const state: GirasolesViewerState = {
      camera: {
        position: {
          x: camera.position.x,
          y: camera.position.y,
          z: camera.position.z
        },
        rotation: {
          x: camera.rotation.x,
          y: camera.rotation.y,
          z: camera.rotation.z
        },
        zoom: camera.zoom
      },
      selectedPoints,
      sceneObjects: sceneObjects.map(obj => ({
        id: obj.id,
        visible: obj.visible,
        // Add transform if the object has position/rotation/scale
        transform: obj.transform ? {
          position: { x: obj.transform.position.x, y: obj.transform.position.y, z: obj.transform.position.z },
          rotation: { x: obj.transform.rotation.x, y: obj.transform.rotation.y, z: obj.transform.rotation.z },
          scale: { x: obj.transform.scale.x, y: obj.transform.scale.y, z: obj.transform.scale.z }
        } : undefined
      })),
      selectedTool,
      timestamp: Date.now(),
      version: STATE_VERSION
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    console.log('Viewer state saved successfully')
  } catch (error) {
    console.error('Failed to save viewer state:', error)
  }
}

/**
 * Load the viewer state from localStorage
 */
export const loadViewerState = (): GirasolesViewerState | null => {
  const state = readValidatedJSON(STORAGE_KEY, isGirasolesViewerState)
  if (!state) return null

  if (state.version !== STATE_VERSION) {
    console.warn(`State version mismatch. Expected ${STATE_VERSION}, got ${state.version}. Clearing state.`)
    clearViewerState()
    return null
  }

  return state
}

/**
 * Apply camera state to a Three.js camera
 */
export const applyCameraState = (camera: THREE.PerspectiveCamera, cameraState: CameraState): void => {
  camera.position.set(cameraState.position.x, cameraState.position.y, cameraState.position.z)
  camera.rotation.set(cameraState.rotation.x, cameraState.rotation.y, cameraState.rotation.z)
  camera.zoom = cameraState.zoom
  camera.updateProjectionMatrix()
}

/**
 * Clear the saved viewer state
 */
export const clearViewerState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
    console.log('Viewer state cleared')
  } catch (error) {
    console.error('Failed to clear viewer state:', error)
  }
}

/**
 * Check if there is a saved state available
 */
export const hasSavedState = (): boolean => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY)
    return savedState !== null
  } catch (error) {
    return false
  }
}

/**
 * Auto-save state with debouncing to prevent excessive saves
 */
let saveTimeout: NodeJS.Timeout | null = null

export const autoSaveViewerState = (
  camera: THREE.PerspectiveCamera,
  selectedPoints: SelectedPoint[],
  sceneObjects: any[],
  selectedTool: string | null,
  debounceMs: number = 1000
): void => {
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }

  saveTimeout = setTimeout(() => {
    saveViewerState(camera, selectedPoints, sceneObjects, selectedTool)
  }, debounceMs)
}
